import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { formatCurrency } from '@shared/formatCurrency';
import { formatCoveragePercentage } from '@/features/shop-insurance/lib/coverage-gap-utils';

export interface PremiumLimitNotice {
  label: string;
  qualifier?: 'max' | 'min';
  explanation: string;
}

interface QuotePriceDisplayProps {
  price: number;
  fundingOption?: string;
  label?: string;
  className?: string;
  coveragePercentage?: number;
  premiumLimit?: PremiumLimitNotice;
}

export function QuotePriceDisplay({
  price,
  fundingOption,
  label = 'Cost',
  className = '',
  coveragePercentage,
  premiumLimit,
}: QuotePriceDisplayProps) {
  const formattedPrice = formatCurrency(price);

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div
        data-testid="quote-card-cost-display"
        className="text-primary-dark flex items-center gap-2 text-sm font-semibold sm:text-base"
      >
        {premiumLimit ? (
          <Tooltip>
            <TooltipTrigger
              className="inline-flex cursor-help items-center text-red-700 underline decoration-dotted decoration-1 underline-offset-4"
              data-testid="premium-clamped-badge"
              aria-label={`${formattedPrice}, ${premiumLimit.label.toLowerCase()}`}
            >
              {formattedPrice}
              {premiumLimit.qualifier && ` (${premiumLimit.qualifier})`}
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="center"
              className="max-w-80 whitespace-normal"
            >
              <span className="font-semibold">{premiumLimit.label}.</span>{' '}
              {premiumLimit.explanation}
            </TooltipContent>
          </Tooltip>
        ) : (
          formattedPrice
        )}
        {coveragePercentage !== undefined && (
          <Badge variant="success">{formatCoveragePercentage(coveragePercentage)}% Coverage</Badge>
        )}
      </div>
      {fundingOption && <div className="text-muted-foreground text-xs italic">{fundingOption}</div>}
    </div>
  );
}
